/* eslint-disable @typescript-eslint/no-explicit-any */
// Type guards need to use 'any' to properly validate unknown data
import {
  ActivityResponseDto,
  AuthResponseDto,
  BalanceResponseDto,
  ErrorResponseDto,
  ExpenseResponseDto,
  GroupResponseDto,
  GroupWithDashboard,
  NotificationResponseDto,
  SettlementResponseDto,
  SuccessResponseDto,
  UserResponseDto,
} from '@splitfiesta/shared';

/**
 * Type guard for AuthResponseDto
 */
export function isAuthResponse(data: unknown): data is AuthResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'user' in data &&
    'accessToken' in data &&
    typeof (data as any).user === 'object' &&
    typeof (data as any).accessToken === 'string'
  );
}

/**
 * Type guard for ErrorResponseDto
 */
export function isErrorResponse(data: unknown): data is ErrorResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as any).error === 'object' &&
    (data as any).error !== null &&
    'message' in (data as any).error &&
    typeof (data as any).error.message === 'string'
  );
}

/**
 * Type guard for UserResponseDto
 */
export function isUserResponse(data: unknown): data is UserResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'email' in data &&
    typeof (data as any).id === 'string' &&
    typeof (data as any).email === 'string'
  );
}

/**
 * Type guard for GroupResponseDto
 */
export function isGroupResponse(data: unknown): data is GroupResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'name' in data &&
    'currency' in data &&
    typeof (data as any).id === 'string' &&
    typeof (data as any).name === 'string'
  );
}

/**
 * Type guard for GroupWithDashboard
 */
export function isGroupWithDashboard(
  data: unknown
): data is GroupWithDashboard {
  return (
    isGroupResponse(data) &&
    'dashboard' in data &&
    typeof (data as any).dashboard === 'object'
  );
}

/**
 * Type guard for ExpenseResponseDto
 */
export function isExpenseResponse(data: unknown): data is ExpenseResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'groupId' in data &&
    'title' in data &&
    'amount' in data &&
    'payers' in data &&
    'splits' in data &&
    typeof (data as any).id === 'string' &&
    typeof (data as any).groupId === 'string' &&
    typeof (data as any).title === 'string' &&
    typeof (data as any).amount === 'number' &&
    Array.isArray((data as any).payers) &&
    Array.isArray((data as any).splits)
  );
}

/**
 * Type guard for BalanceResponseDto
 */
export function isBalanceResponse(data: unknown): data is BalanceResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'userId' in data &&
    'balance' in data &&
    typeof (data as any).userId === 'string' &&
    typeof (data as any).balance === 'number'
  );
}

/**
 * Type guard for ActivityResponseDto
 */
export function isActivityResponse(data: unknown): data is ActivityResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'groupId' in data &&
    'type' in data &&
    'timestamp' in data &&
    typeof (data as any).id === 'string' &&
    typeof (data as any).type === 'string'
  );
}

/**
 * Type guard for NotificationResponseDto
 */
export function isNotificationResponse(
  data: unknown
): data is NotificationResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'userId' in data &&
    'type' in data &&
    'message' in data &&
    typeof (data as any).id === 'string' &&
    typeof (data as any).userId === 'string'
  );
}

/**
 * Type guard for SettlementResponseDto
 */
export function isSettlementResponse(
  data: unknown
): data is SettlementResponseDto {
  return (
    typeof data === 'object' &&
    data !== null &&
    'id' in data &&
    'groupId' in data &&
    'fromUser' in data &&
    'toUser' in data &&
    'amount' in data &&
    typeof (data as any).id === 'string' &&
    typeof (data as any).amount === 'number'
  );
}

/**
 * Type guard for SuccessResponseDto with generic data
 */
export function isSuccessResponse<T = unknown>(
  data: unknown,
  dataGuard?: (data: unknown) => data is T
): data is SuccessResponseDto<T> {
  if (
    typeof data !== 'object' ||
    data === null ||
    !('success' in data) ||
    !('data' in data)
  ) {
    return false;
  }

  const response = data as any;

  if (response.success !== true) {
    return false;
  }

  // If a data guard is provided, validate the data field
  if (dataGuard && response.data !== undefined) {
    return dataGuard(response.data);
  }

  return true;
}

/**
 * Type guard for arrays of responses
 */
export function isArrayOf<T>(
  data: unknown,
  itemGuard: (item: unknown) => item is T
): data is T[] {
  return Array.isArray(data) && data.every(itemGuard);
}

/**
 * Helper to safely extract data from API responses
 */
export function extractResponseData<T>(
  response: unknown,
  guard: (data: unknown) => data is T
): T | null {
  if (isSuccessResponse(response) && guard((response as any).data)) {
    return (response as SuccessResponseDto<T>).data;
  }
  return null;
}

/**
 * Helper to check if response is an error
 */
export function extractErrorMessage(response: unknown): string | null {
  if (isErrorResponse(response)) {
    return response.error.message;
  }
  if (
    typeof response === 'object' &&
    response !== null &&
    'message' in response
  ) {
    return String((response as any).message);
  }
  return null;
}
